import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Utensils } from "lucide-react";
import { useTranslation } from "react-i18next";
import Prisma from "@prisma/client";
import { useIsMobile } from "@/hooks/use-mobile";
interface MealsTableProps {
    meals: Prisma.Meal[];
    totalCalories: number;
    availableCalories: number;
}

export const MealsTable = ({ meals, totalCalories, availableCalories }: MealsTableProps) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    const getDifferenceMessage = () => {
        if (availableCalories > 0) {
            return t("dashboard.meals.underTarget");
        } else if (availableCalories < 0) {
            return t("dashboard.meals.overTarget");
        }
        return t("dashboard.meals.onTarget");
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{t("dashboard.meals.todayMeals")}</h2>
                <Utensils className="text-primary" size={24} />
            </div>

            <Table className="w-full">
                <TableHeader>
                    <TableRow className="w-full">
                        <TableHead className={isMobile ? "w-[30%]" : "md:w-[10%]"}>{t("dashboard.meals.type")}</TableHead>
                        {!isMobile && (
                            <>
                                <TableHead className="w-[50%]">{t("dashboard.meals.meal")}</TableHead>
                                <TableHead className="w-[10%]">{t("dashboard.meals.location")}</TableHead>
                            </>
                        )}
                        <TableHead className={"md:pr-12 text-right" + (isMobile ? "w-[30%]" : "md:w-[10%]")}>
                            {t("dashboard.meals.analitycs")}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {meals.map((meal) => (
                        <TableRow key={meal.id}>
                            <TableCell className="font-medium align-top">{t(`meals.types.${meal.mealType}`)}</TableCell>
                            {!isMobile && (
                                <>
                                    <TableCell className="align-top hidden md:table-cell">
                                        <span dangerouslySetInnerHTML={{ __html: meal.meal.replace(/\n/g, "<br>") }} />
                                    </TableCell>
                                    <TableCell className="align-top hidden md:table-cell">
                                        {t(`meals.locations.${meal.location}`)}
                                    </TableCell>
                                </>
                            )}
                            <TableCell className="text-right align-top">
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: `
                        ${meal.calories} <span class="text-left inline-block w-[5em]">${t("meals.kcal")}</span><br>
                        ${meal.carbs} <span class="text-left inline-block w-[5em]">${t("meals.carbs")}</span><br>
                        ${meal.proteins} <span class="text-left inline-block w-[5em]">${t("meals.proteins")}</span><br>
                        ${meal.fats} <span class="text-left inline-block w-[5em]">${t("meals.fats")}</span>`,
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-muted/50">
                        <TableCell colSpan={isMobile ? 1 : 3} className="font-bold">
                            {t("dashboard.meals.totalCalories")}
                        </TableCell>
                        <TableCell className="text-right font-bold">{totalCalories} kcal</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={isMobile ? 1 : 3} className="font-bold">
                            {t("dashboard.meals.targetDifference")}
                        </TableCell>
                        <TableCell
                            className={`text-right font-bold ${
                                availableCalories > 0 ? "text-green-600" : availableCalories < 0 ? "text-red-600" : "text-gray-600"
                            }`}
                        >
                            {Math.abs(availableCalories)} kcal ({getDifferenceMessage()})
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
};
